import { Link } from '@tanstack/react-router';
import { Calendar, Clock, Loader2, Users } from 'lucide-react';

import { useGetClassAnalytics } from '~entities/classes/api/class-analytics';
import { useGetClass } from '~entities/classes/api/get-class';

import { AttendanceExportDropdown } from '~shared/components/attendance-export-dropdown';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { convertTo12HourFormat } from '~shared/utils/date-time';

export interface MetricStripProps {
  classId: string;
}

export const MetricStrip = ({ classId }: MetricStripProps) => {
  const { data: classDetails, isLoading: isLoadingClassDetails } = useGetClass({
    classId,
  });

  const { data, isLoading } = useGetClassAnalytics({ enrollmentId: classId });

  if (isLoading || isLoadingClassDetails)
    return (
      <div className='flex h-32 items-center justify-center'>
        <Loader2 className='animate-spin' />
      </div>
    );

  if (!data) return null;

  const { totalStudents, averageAttendance, totalClasses, classDuration } =
    data.resData;

  const scheduleString = `${(classDetails?.days ?? []).join(', ')} at ${
    classDetails?.startTime ? convertTo12HourFormat(classDetails.startTime) : ''
  }`;

  return (
    <Card className='mb-6'>
      <CardHeader className='p-2 sm:p-6'>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <CardTitle className='text-2xl'>{classDetails?.title}</CardTitle>
          <div className='flex flex-wrap items-center gap-2'>
            <AttendanceExportDropdown
              entityId={classId}
              entityTitle={classDetails?.title}
            />
            <Button asChild>
              <Link
                to={`/attendance/$classId`}
                params={{
                  classId: classId,
                }}
              >
                Mark Attendance
              </Link>
            </Button>
          </div>
        </div>
        <CardDescription className='mt-1'>
          {classDetails?.instructor?.[0]?.fieldName && (
            <>Instructor: {classDetails.instructor[0].fieldName}</>
          )}
          {classDetails?.instructor?.[0]?.fieldName &&
            classDetails?.session?.name && <span className='mx-1'> • </span>}
          {classDetails?.session?.name && (
            <>Session: {classDetails.session.name}</>
          )}
          {(classDetails?.instructor?.[0]?.fieldName ??
            classDetails?.session?.name) &&
            classDetails.room?.fieldName && <span className='mx-1'> • </span>}
          {classDetails?.room?.fieldName && (
            <>Room: {classDetails.room.fieldName}</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className='p-2 sm:p-6'>
        <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-4'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='text-sm font-medium'>
                Enrolled Students
              </CardTitle>
              <Users className='size-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{totalStudents}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='text-sm font-medium'>
                Average Attendance
              </CardTitle>
              <Calendar className='size-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{averageAttendance}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='text-sm font-medium'>
                Class Duration
              </CardTitle>
              <Clock className='size-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{classDuration} mins</div>
              <p className='text-xs text-muted-foreground'>{scheduleString}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='text-sm font-medium'>
                Total Classes
              </CardTitle>
              <Calendar className='size-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{totalClasses}</div>
              <p className='text-xs text-muted-foreground'>
                Total scheduled classes
              </p>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};
